/**
 * Shipping Progress Visibility
 *
 * The free shipping progress bar tracks how much more a customer must spend to unlock free
 * delivery. Store pickup is always free, so the bar carries no meaning there and is hidden
 * on every surface that renders it — the cart visor and the cart page.
 *
 * Liquid stamps `is-pickup` on the bar at render time from the `delivery_preference` cart
 * attribute (see src/sections/main-cart-items.liquid and src/snippets/cart-notification.liquid).
 * This module keeps that class correct afterwards, for two cases Liquid cannot cover:
 *
 * 1. The customer switches option without a page load — `deliveryPreference:changed` fires.
 * 2. A bar is replaced by a section fetch — the replacement may carry a stale class, because
 *    the cart attribute is written asynchronously and can lag the localStorage preference.
 *
 * localStorage is the authority here, not the cart attribute, since it is written first.
 *
 * Exposes `window.ShippingProgressVisibility.apply()` so the two section-fetch handlers can
 * re-apply after they swap the markup:
 * - src/assets/cart-notification.js (cart visor)
 * - src/layout/theme.liquid (cart page)
 */
(function () {
  'use strict';

  var PICKUP_CLASS = 'is-pickup';
  var BAR_SELECTOR = '.free-shipping-progress-bar';
  var STORAGE_KEY = 'deliveryPreference';

  /**
   * Read the stored preference, distinguishing "no preference stored" from "delivery".
   * DeliverySelector.getDeliveryPreference() cannot be used here: it returns DELIVERY when
   * nothing is stored, which is the right default for the selector and the wrong answer for
   * this module. localStorage is read directly so an absent value stays absent.
   * @returns {'STORE'|'DELIVERY'|null} null when nothing readable is stored
   */
  function storedPreference() {
    try {
      var stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      var type = JSON.parse(stored).deliveryType;
      return type === 'STORE' || type === 'DELIVERY' ? type : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Is the customer currently set to store pickup? Absent storage counts as delivery, matching
   * the default the delivery selector applies. Callers that must not act on a guess should use
   * storedPreference() instead.
   * @returns {boolean}
   */
  function isPickup() {
    return storedPreference() === 'STORE';
  }

  /**
   * Stamp or clear `is-pickup` on every progress bar in the document.
   *
   * When nothing is stored, leave every bar exactly as Liquid rendered it. Liquid stamped the
   * class from the delivery_preference cart attribute, which survives storage the browser has
   * cleared, blocked or partitioned — overwriting it from an absent localStorage would show a
   * free delivery bar to a customer who has chosen pickup.
   */
  function apply() {
    var preference = storedPreference();
    if (preference === null) return;

    var pickup = preference === 'STORE';
    var bars = document.querySelectorAll(BAR_SELECTOR);

    for (var i = 0; i < bars.length; i++) {
      bars[i].classList.toggle(PICKUP_CLASS, pickup);
    }
  }

  window.ShippingProgressVisibility = { apply: apply, isPickup: isPickup, storedPreference: storedPreference };

  document.addEventListener('deliveryPreference:changed', apply);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
})();
